import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCompany } from '../../hooks/useCompany';
import { Modal } from '../ui/Modal';
import { PdfUpload } from '../pdf/PdfUpload';
import { ProfileCard } from '../profiles/ProfileCard';
import { ProfileForm } from '../profiles/ProfileForm';
import { apiClient } from '../../api/client';
import { ContactList } from '../contacts/ContactList';
import { AcquisitionProfile, Company, Contact } from '../../types';
import { CompanyForm } from './CompanyForm';
import { useDeleteCompany } from '../../hooks/useCompanies';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';

function formatVolume(min?: number | null, max?: number | null): string {
  if (!min && !max) return '—';
  const fmt = (v: number) => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(0)} Mio.`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(0)} Tsd.`;
    return `${v}`;
  };
  if (min && max) return `${fmt(min)} – ${fmt(max)} €`;
  if (min) return `ab ${fmt(min)} €`;
  return `bis ${fmt(max!)} €`;
}

interface CompanyDetailProps {
  id: string;
}

export function CompanyDetail({ id }: CompanyDetailProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, error, refetch } = useCompany(id);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [webResearching, setWebResearching] = useState(false);
  const [webResearchError, setWebResearchError] = useState<string | null>(null);
  const [webResearchSuccess, setWebResearchSuccess] = useState(false);
  const [contactResearching, setContactResearching] = useState(false);
  const [contactResearchError, setContactResearchError] = useState<string | null>(null);
  const [contactResearchResult, setContactResearchResult] = useState<{ created: number; found: number; message?: string } | null>(null);
  const { user } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteCompanyModal, setShowDeleteCompanyModal] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState<'verify' | 'approve' | null>(null);
  const [refreshingContacts, setRefreshingContacts] = useState(false);
  const [convertingEinzelfirma, setConvertingEinzelfirma] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<AcquisitionProfile | null>(null);
  const [editingProfile, setEditingProfile] = useState(false);
  // wurde Profil per Deep-Link (?openProfile=) geöffnet? → beim Close den Tab schließen
  const [profileOpenedFromDeepLink, setProfileOpenedFromDeepLink] = useState(false);

  // Deep-Link: ?openProfile=<id> → Profile-Modal direkt öffnen
  useEffect(() => {
    const openProfileId = searchParams.get('openProfile');
    const profiles = data?.data?.profiles;
    if (openProfileId && profiles) {
      const profile = profiles.find((p: AcquisitionProfile) => p.id === openProfileId);
      if (profile) {
        setSelectedProfile(profile);
        setEditingProfile(false);
        setProfileOpenedFromDeepLink(true);
        const sp = new URLSearchParams(searchParams);
        sp.delete('openProfile');
        setSearchParams(sp, { replace: true });
      }
    }
  }, [searchParams, data?.data?.profiles, setSearchParams]);

  /**
   * Schließt Profil-Modal. Wenn das Modal per Deep-Link geöffnet wurde
   * (also der Tab nur dafür da ist), schließe stattdessen den Tab.
   */
  const closeProfileModal = () => {
    if (profileOpenedFromDeepLink) {
      window.close();
      // Falls window.close blockt (z.B. Tab nicht via window.open geöffnet):
      // navigiere zurück bzw. zeige normale CompanyDetail.
      setProfileOpenedFromDeepLink(false);
    }
    setSelectedProfile(null);
    setEditingProfile(false);
  };
  const [deleteProfileId, setDeleteProfileId] = useState<string | null>(null);
  const [deletingProfile, setDeletingProfile] = useState(false);
  const { mutate: deleteCompany, isPending: deletingCompany } = useDeleteCompany();
  const queryClient = useQueryClient();
  const invalidateList = () => queryClient.invalidateQueries({ queryKey: ['companies'] });

  // Einzelfirma → echte Firma: entfernt Flag + Platzhalter-Kontakt, öffnet danach Edit
  const handleConvertEinzelfirma = async () => {
    if (!confirm('Diese Einzelfirma in eine echte Firma umwandeln?\n\nDer Platzhalter-Kontakt (falls vorhanden) wird entfernt. Danach kannst du Firmenname/Rechtsform bearbeiten und Ansprechpartner über „Internet recherchieren" finden.')) return;
    setConvertingEinzelfirma(true);
    try {
      const { data: resp } = await apiClient.post(`/companies/${id}/convert-to-real`);
      invalidateList();
      await queryClient.invalidateQueries({ queryKey: ['contacts'] });
      refetch();
      const n = resp?.deleted_placeholder_contacts ?? 0;
      if (n > 0) alert(`Umgewandelt. ${n} Platzhalter-Kontakt(e) entfernt.\n\nJetzt bitte Firmennamen/Rechtsform prüfen und Ansprechpartner per Internet-Recherche suchen.`);
      // direkt Bearbeiten öffnen
      setShowEditModal(true);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      alert(msg ?? 'Fehler beim Umwandeln.');
    } finally {
      setConvertingEinzelfirma(false);
    }
  };

  const handleRefreshContacts = async () => {
    if (!data?.data) return;
    setRefreshingContacts(true);
    setContactResearchError(null);
    setContactResearchResult(null);
    try {
      const { data: result } = await apiClient.post('/contacts/research-web', {
        company_id: id,
        update_existing: true,
      });
      setContactResearchResult({
        created: result.created_count ?? 0,
        found: result.found_count ?? 0,
        message: result.updated_count > 0
          ? `${result.found_count} Führungskräfte gefunden — ${result.updated_count} aktualisiert, ${result.created_count} neu angelegt.`
          : result.message ?? null,
      });
      await queryClient.invalidateQueries({ queryKey: ['contacts'] });
      await queryClient.invalidateQueries({ queryKey: ['company', id] });
      refetch();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setContactResearchError(msg ?? 'Fehler beim Aktualisieren.');
    } finally {
      setRefreshingContacts(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!deleteProfileId) return;
    setDeletingProfile(true);
    try {
      await apiClient.delete(`/profiles/${deleteProfileId}`);
      setDeleteProfileId(null);
      refetch();
    } finally {
      setDeletingProfile(false);
    }
  };
  const handleContactResearch = async (company: Company) => {
    setContactResearching(true);
    setContactResearchError(null);
    setContactResearchResult(null);
    try {
      const { data } = await apiClient.post('/contacts/research-web', { company_id: company.id });
      setContactResearchResult({
        created: data.created_count ?? 0,
        found: data.found_count ?? 0,
        message: data.message ?? null,
      });
      refetch();
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setContactResearchError(msg ?? 'Fehler bei der Web-Recherche.');
    } finally {
      setContactResearching(false);
    }
  };

  const handleWebResearch = async (company: Company) => {
    if (!company.website) {
      setWebResearchError('Keine Website hinterlegt. Bitte die Website in den Stammdaten des Investors pflegen.');
      return;
    }
    setWebResearching(true);
    setWebResearchError(null);
    setWebResearchSuccess(false);
    try {
      await apiClient.post('/profiles/research-web', { company_id: company.id });
      setWebResearchSuccess(true);
      refetch();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setWebResearchError(msg ?? 'Fehler bei der Web-Recherche.');
    } finally {
      setWebResearching(false);
    }
  };

  const [activeTab, setActiveTab] = useState<'profiles' | 'contacts'>('profiles');

  const handleVerify = async (action: 'verify' | 'approve') => {
    setStatusUpdating(action);
    try {
      await apiClient.post(`/companies/${id}/${action}`, { reviewer_name: user?.full_name ?? user?.email ?? 'Unbekannt' });
      refetch();
      void queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
    } finally {
      setStatusUpdating(null);
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text3)' }}>
        Laden...
      </div>
    );
  }

  if (error || !data?.data) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: 'var(--accent)' }}>
        Firma nicht gefunden.
      </div>
    );
  }

  const company: Company = data.data;
  const contacts: Contact[] = (company.contacts as Contact[]) ?? [];
  const profiles: AcquisitionProfile[] = (company.profiles as AcquisitionProfile[]) ?? [];

  return (
    <div>
      {/* Back */}
      <button
        className="btn"
        onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/companies')}
        style={{ marginBottom: '16px' }}
      >
        ← Zurück
      </button>

      {/* Header card */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                {(() => {
                  const src = company.logo_url ||
                    (company.website ? (() => { try { return `https://www.google.com/s2/favicons?domain=${new URL(company.website!).hostname}&sz=128`; } catch { return null; } })() : null);
                  const initial = (company.display_name || '?').trim().charAt(0).toUpperCase();
                  return (
                    <div style={{
                      width: 32, height: 32, borderRadius: 4, flexShrink: 0,
                      background: 'var(--surface2)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      fontSize: 14, fontWeight: 600, color: 'var(--text2)',
                      position: 'relative', overflow: 'hidden',
                    }}>
                      {initial}
                      {src && (
                        <img src={src} alt=""
                          style={{
                            position: 'absolute', inset: 0, width: '100%', height: '100%',
                            objectFit: 'contain', background: '#fff',
                          }}
                          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      )}
                    </div>
                  );
                })()}
                <h1 style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text)' }}>
                  {company.display_name}
                </h1>
              </div>
              {company.legal_name && (
                <div style={{ fontSize: '13px', color: 'var(--text2)' }}>{company.legal_name}</div>
              )}
              <div style={{ display: 'flex', gap: '16px', marginTop: '8px', fontSize: '12px', color: 'var(--text3)' }}>
                {company.company_type && <span>{company.company_type}</span>}
                {(company.street || company.postal_code || company.city) && (
                  <span>
                    {company.street && `${company.street}, `}
                    {company.postal_code && `${company.postal_code} `}
                    {company.city}
                    {company.country_code ? ` · ${company.country_code}` : ''}
                  </span>
                )}
                {company.website && (
                  <a href={company.website} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--info)' }}>
                    {company.website}
                  </a>
                )}
              </div>
              {company.description && (
                <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text2)' }}>
                  {company.description}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '8px', flexShrink: 0, flexWrap: 'wrap' }}>
              {company.ist_einzelfirma && (
                <button
                  onClick={handleConvertEinzelfirma}
                  disabled={convertingEinzelfirma}
                  title="Entfernt das Einzelfirma-Flag, löscht den Platzhalter-Kontakt und öffnet den Bearbeiten-Dialog."
                  style={{
                    padding: '7px 14px', background: 'var(--accent)', color: '#fff',
                    border: 'none', borderRadius: 'var(--radius)',
                    fontSize: '13px', fontWeight: 500,
                    cursor: convertingEinzelfirma ? 'wait' : 'pointer',
                    opacity: convertingEinzelfirma ? 0.7 : 1,
                  }}
                >
                  {convertingEinzelfirma ? '⏳ Wird umgewandelt…' : '↻ In echte Firma umwandeln'}
                </button>
              )}
              <a
                href={`https://www.handelsregister.de/rp_web/search.xhtml?Firmenname=${encodeURIComponent(company.legal_name || company.display_name)}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Im deutschen Handelsregister suchen (öffnet in neuem Tab)"
                style={{
                  padding: '7px 14px', background: 'var(--surface)', border: '1px solid var(--border2)',
                  borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text2)',
                  cursor: 'pointer', textDecoration: 'none', display: 'inline-block',
                }}
              >
                🏛 Handelsregister
              </a>
              <button
                onClick={() => setShowEditModal(true)}
                style={{
                  padding: '7px 14px', background: 'var(--surface)', border: '1px solid var(--border2)',
                  borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text2)', cursor: 'pointer',
                }}
              >
                Bearbeiten
              </button>
              <button
                onClick={() => setShowDeleteCompanyModal(true)}
                style={{
                  padding: '7px 14px', background: 'var(--surface)', border: '1px solid var(--border2)',
                  borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text3)', cursor: 'pointer',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text3)'; }}
              >
                Löschen
              </button>
            </div>
          </div>

          {/* Tags */}
          {company.tags_array && company.tags_array.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '12px' }}>
              {company.tags_array.filter(Boolean).map((tag: string, i: number) => (
                <span key={i} style={{
                  padding: '2px 8px',
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: '20px',
                  fontSize: '11px',
                  color: 'var(--text2)',
                }}>
                  {tag.trim()}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Stats bar + Status-Buttons */}
        <div style={{
          borderTop: '1px solid var(--border)',
          padding: '10px 24px',
          display: 'flex',
          gap: '20px',
          alignItems: 'center',
          background: 'var(--surface2)',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '12px', color: 'var(--text3)' }}>
            <span style={{ fontFamily: 'DM Mono, monospace', fontWeight: 500, color: 'var(--accent)' }}>
              {profiles.length}
            </span>
            {' '}Ankaufsprofile
          </span>
          <span style={{ fontSize: '12px', color: 'var(--text3)' }}>
            <span style={{ fontFamily: 'DM Mono, monospace', fontWeight: 500, color: 'var(--text)' }}>
              {contacts.length}
            </span>
            {' '}Ansprechpartner
          </span>

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
            {/* Geprüft */}
            {company.verified_at ? (
              <span style={{ fontSize: '11px', color: '#2e7d32', background: '#e8f5e9', padding: '3px 10px', borderRadius: '20px', display: 'flex', gap: '5px', alignItems: 'center' }}>
                ✓ Geprüft · {new Date(company.verified_at).toLocaleDateString('de-DE')}
                {company.verified_by_name && <span style={{ color: '#4caf50' }}>von {company.verified_by_name}</span>}
              </span>
            ) : (
              <button
                onClick={() => handleVerify('verify')}
                disabled={statusUpdating !== null}
                style={{ padding: '4px 14px', fontSize: '12px', fontWeight: 500, border: '1px solid #a5d6a7', borderRadius: '20px', background: '#fff', color: '#2e7d32', cursor: 'pointer', opacity: statusUpdating ? 0.6 : 1 }}
              >
                {statusUpdating === 'verify' ? '…' : '✓ Als geprüft markieren'}
              </button>
            )}

            {/* Freigegeben — nur möglich wenn bereits geprüft */}
            {company.approved_at ? (
              <span style={{ fontSize: '11px', color: '#1565c0', background: '#e3f2fd', padding: '3px 10px', borderRadius: '20px', display: 'flex', gap: '5px', alignItems: 'center' }}>
                ✓ Freigegeben · {new Date(company.approved_at).toLocaleDateString('de-DE')}
                {company.approved_by_name && <span style={{ color: '#1976d2' }}>von {company.approved_by_name}</span>}
              </span>
            ) : company.verified_at ? (
              <button
                onClick={() => handleVerify('approve')}
                disabled={statusUpdating !== null}
                style={{ padding: '4px 14px', fontSize: '12px', fontWeight: 500, border: '1px solid #90caf9', borderRadius: '20px', background: '#fff', color: '#1565c0', cursor: 'pointer', opacity: statusUpdating ? 0.6 : 1 }}
              >
                {statusUpdating === 'approve' ? '…' : '↗ Freigeben'}
              </button>
            ) : (
              <span title="Erst prüfen, dann freigeben" style={{ fontSize: '11px', color: 'var(--text3)', background: 'var(--surface2)', padding: '3px 10px', borderRadius: '20px', cursor: 'not-allowed' }}>
                ↗ Freigeben (erst prüfen)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
        {(['profiles', 'contacts'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '7px 16px',
              border: 'none',
              borderRadius: 'var(--radius)',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
              background: activeTab === tab ? 'var(--accent)' : 'transparent',
              color: activeTab === tab ? '#fff' : 'var(--text2)',
            }}
          >
            {tab === 'profiles' ? `Ankaufsprofile (${profiles.length})` : `Ansprechpartner (${contacts.length})`}
          </button>
        ))}
      </div>

      {/* Profiles */}
      {activeTab === 'profiles' && (
        <div>
          {/* Profile toolbar */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            <button
              onClick={() => setShowProfileModal(true)}
              style={{ padding: '7px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}
            >
              + Profil manuell anlegen
            </button>
            <button
              onClick={() => setShowPdfModal(true)}
              style={{ padding: '7px 14px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text2)', cursor: 'pointer' }}
            >
              PDF importieren
            </button>
            <button
              onClick={() => { setWebResearchError(null); setWebResearchSuccess(false); handleWebResearch(company); }}
              disabled={webResearching}
              style={{
                padding: '7px 14px', borderRadius: 'var(--radius)', fontSize: '13px', cursor: webResearching ? 'not-allowed' : 'pointer',
                background: webResearching ? 'var(--surface2)' : '#e8f5e9',
                color: webResearching ? 'var(--text3)' : '#2e7d32',
                border: '1px solid',
                borderColor: webResearching ? 'var(--border2)' : '#a5d6a7',
                opacity: webResearching ? 0.8 : 1,
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              {webResearching ? (
                <><span style={{ fontSize: '12px' }}>⏳</span> Recherchiert...</>
              ) : (
                <><span style={{ fontSize: '12px' }}>🌐</span> In Internet recherchieren</>
              )}
            </button>
          </div>

          {/* Web research feedback */}
          {webResearchError && (
            <div style={{ padding: '10px 14px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px', marginBottom: '8px' }}>
              ⚠️ {webResearchError}
            </div>
          )}
          {webResearchSuccess && (
            <div style={{ padding: '10px 14px', background: '#e8f5e9', color: '#2e7d32', borderRadius: 'var(--radius)', fontSize: '13px', marginBottom: '8px' }}>
              ✓ Profil erfolgreich aus Website erstellt.
            </div>
          )}

          {profiles.length === 0 ? (
            <div className="card" style={{ padding: '60px', textAlign: 'center', color: 'var(--text3)' }}>
              Noch keine Ankaufsprofile.<br />
              <span style={{ fontSize: '12px' }}>Manuell anlegen oder PDF importieren.</span>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '12px' }}>
              {profiles.map((profile: AcquisitionProfile) => (
                <div
                  key={profile.id}
                  onClick={() => { setSelectedProfile(profile); setEditingProfile(false); }}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = '0.85')}
                  onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
                >
                  <ProfileCard profile={profile} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Contacts */}
      {activeTab === 'contacts' && (
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => { setContactResearchError(null); setContactResearchResult(null); handleContactResearch(company); }}
              disabled={contactResearching}
              style={{
                padding: '7px 14px', borderRadius: 'var(--radius)', fontSize: '13px',
                cursor: contactResearching ? 'not-allowed' : 'pointer',
                background: contactResearching ? 'var(--surface2)' : '#e8f5e9',
                color: contactResearching ? 'var(--text3)' : '#2e7d32',
                border: '1px solid', borderColor: contactResearching ? 'var(--border2)' : '#a5d6a7',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              {contactResearching ? '⏳ Recherchiert...' : '🌐 In Internet recherchieren'}
            </button>
            {contacts.length > 0 && (
              <button
                type="button"
                disabled={refreshingContacts}
                onClick={handleRefreshContacts}
                style={{
                  padding: '7px 14px', borderRadius: 'var(--radius)', fontSize: '13px',
                  cursor: refreshingContacts ? 'not-allowed' : 'pointer',
                  background: 'var(--surface)',
                  color: refreshingContacts ? 'var(--text3)' : 'var(--text2)',
                  border: '1px solid var(--border2)',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  opacity: refreshingContacts ? 0.7 : 1,
                }}
              >
                {refreshingContacts ? '⏳ Aktualisiert...' : '↻ Aktualisieren'}
              </button>
            )}
          </div>

          {contactResearchError && (
            <div style={{ padding: '10px 14px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px', marginBottom: '10px' }}>
              ⚠️ {contactResearchError}
            </div>
          )}
          {contactResearchResult && (
            <div style={{ padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: '13px', marginBottom: '10px',
              background: contactResearchResult.created > 0 ? '#e8f5e9' : '#fff3e0',
              color: contactResearchResult.created > 0 ? '#2e7d32' : '#e65100',
            }}>
              {contactResearchResult.message
                ? `ℹ️ ${contactResearchResult.message}`
                : `✓ ${contactResearchResult.found} Führungskräfte gefunden — ${contactResearchResult.created} neu angelegt und verknüpft.`}
            </div>
          )}

          <ContactList companyId={id} />
        </div>
      )}

      {/* Modal: Firma löschen */}
      <Modal isOpen={showDeleteCompanyModal} onClose={() => setShowDeleteCompanyModal(false)} title="Investor löschen" size="sm">
        <div style={{ fontSize: '14px', color: 'var(--text)', marginBottom: '20px' }}>
          <strong>{company.display_name}</strong> wirklich löschen?
          <br /><span style={{ color: 'var(--text3)', fontSize: '12px' }}>Alle Profile und Kontakte dieses Investors werden ebenfalls gelöscht.</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button onClick={() => setShowDeleteCompanyModal(false)}
            style={{ padding: '8px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', cursor: 'pointer' }}>
            Abbrechen
          </button>
          <button
            onClick={() => deleteCompany(company.id, { onSuccess: () => navigate('/companies') })}
            disabled={deletingCompany}
            style={{ padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
            {deletingCompany ? 'Löschen...' : 'Löschen'}
          </button>
        </div>
      </Modal>

      {/* Modal: Firma bearbeiten */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Investor bearbeiten" size="md">
        <CompanyForm
          initial={company}
          onSuccess={() => { setShowEditModal(false); refetch(); invalidateList(); }}
          onCancel={() => setShowEditModal(false)}
        />
      </Modal>

      {/* Modal: Profil Detail / Bearbeiten */}
      <Modal
        isOpen={!!selectedProfile}
        onClose={closeProfileModal}
        title={editingProfile ? 'Profil bearbeiten' : (selectedProfile?.profile_name ?? 'Ankaufsprofil')}
        size="lg"
      >
        {selectedProfile && !editingProfile && (
          <div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <button
                onClick={() => setEditingProfile(true)}
                style={{ padding: '7px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text2)', cursor: 'pointer' }}
              >
                Bearbeiten
              </button>
              <button
                onClick={() => { setDeleteProfileId(selectedProfile.id); setSelectedProfile(null); }}
                style={{ padding: '7px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', color: 'var(--text3)', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text3)'; }}
              >
                Löschen
              </button>
            </div>
            <ProfileCard profile={selectedProfile} />
          </div>
        )}
        {selectedProfile && editingProfile && (
          <ProfileForm
            companyId={id}
            initial={selectedProfile}
            existingAssetClassIds={(profiles ?? []).map((p: AcquisitionProfile) => p.asset_class_id).filter((x): x is string => Boolean(x))}
            onSuccess={() => { setSelectedProfile(null); setEditingProfile(false); refetch(); }}
            onCancel={() => setEditingProfile(false)}
          />
        )}
      </Modal>

      {/* Modal: Profil manuell anlegen */}
      <Modal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} title="Ankaufsprofil anlegen" size="lg">
        <ProfileForm
          companyId={id}
          existingAssetClassIds={(profiles ?? []).map((p: AcquisitionProfile) => p.asset_class_id).filter((x): x is string => Boolean(x))}
          onSuccess={() => { setShowProfileModal(false); refetch(); }}
          onCancel={() => setShowProfileModal(false)}
        />
      </Modal>

      {/* Modal: Profil löschen */}
      <Modal isOpen={!!deleteProfileId} onClose={() => setDeleteProfileId(null)} title="Profil löschen" size="sm">
        <div style={{ fontSize: '14px', color: 'var(--text)', marginBottom: '20px' }}>
          Dieses Ankaufsprofil wirklich löschen?
          <br /><span style={{ color: 'var(--text3)', fontSize: '12px' }}>Diese Aktion kann nicht rückgängig gemacht werden.</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          <button onClick={() => setDeleteProfileId(null)}
            style={{ padding: '8px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', cursor: 'pointer' }}>
            Abbrechen
          </button>
          <button onClick={handleDeleteProfile} disabled={deletingProfile}
            style={{ padding: '8px 16px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
            {deletingProfile ? 'Löschen...' : 'Löschen'}
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={showPdfModal}
        onClose={() => setShowPdfModal(false)}
        title="Ankaufsprofil via PDF hinzufügen"
        size="md"
      >
        <PdfUpload
          mode="add"
          companyId={company.id}
          onSuccess={() => {
            setShowPdfModal(false);
            window.location.reload();
          }}
          onCancel={() => setShowPdfModal(false)}
        />
      </Modal>
    </div>
  );
}

export { formatVolume };
